/**
 * The game's one AudioContext and its mixer: every sound goes through here.
 *
 *     engines ─► engine bus ─┐ (cabin muffle)
 *     world   ─► world bus  ─┼─► master ─► limiter ─► speakers
 *     effects ─► effects bus ┘      ▲
 *                 └── reverb send ──┘ (a tunnel's echo, a street's)
 *
 * Nothing sounds before the first key press, click or tap: browsers keep a
 * page quiet until then, so the context is made on that gesture. The sound
 * bank is made in a worker (`workers/soundbank.worker.ts`) and arrives a
 * sound at a time; anything asked for before it has is skipped, never
 * waited for.
 *
 * The volumes and mute are kept per browser. Mute silences at once.
 */

import type { EngineKind } from '../../core/audio/engine.js';
import type { EngineParams } from '../../core/audio/engineDsp.js';
import type { BankSound } from '../../workers/soundbank.worker.js';
import type { EngineMessage, EngineOptions } from '../../workers/engine.worklet.js';
import workletUrl from '../../workers/engine.worklet.ts?worker&url';

export type Bus = 'engine' | 'world' | 'effects';
/** The volumes: the master, each bus, and the car radio's, which plays outside the mixer (`radio.ts`). */
export type Level = Bus | 'master' | 'radio';
export const LEVELS: readonly Level[] = ['master', 'engine', 'world', 'effects', 'radio'];

export interface SoundSettings {
  muted: boolean;
  master: number;
  engine: number;
  world: number;
  effects: number;
  /** The car radio, under the master: a quarter by default, music under the engine. */
  radio: number;
}

const STORAGE_KEY = 'wsg.sound';
const DEFAULTS: SoundSettings = { muted: false, master: 0.75, engine: 1, world: 1, effects: 1, radio: 0.25 };

function loadSettings(): SoundSettings {
  try {
    const raw = JSON.parse(localStorage.getItem(STORAGE_KEY) ?? '{}') as Partial<SoundSettings>;
    const out = { ...DEFAULTS };
    if (typeof raw.muted === 'boolean') out.muted = raw.muted;
    for (const level of LEVELS) {
      const v = raw[level];
      if (typeof v === 'number' && v >= 0 && v <= 1) out[level] = v;
    }
    return out;
  } catch {
    return { ...DEFAULTS };
  }
}

/** Somewhere in the scene, metres. */
export interface At {
  x: number;
  y: number;
  z: number;
}

export interface PlayOptions {
  bus: Bus;
  /** Where it is; omitted, it is in the listener's head (the player's own breath, a door they shut). */
  at?: At;
  gain?: number;
  /** Playback rate: pitch and speed, for variety. */
  rate?: number;
  /** Seconds from now. */
  delay?: number;
  /** Distance at which it is at full gain, metres. */
  near?: number;
  /** Seconds to play a loop for; one-shots play out. */
  seconds?: number;
}

/** A looping bed of sound, its level and filter to set as the world changes. */
export interface Bed {
  gain: GainNode;
  filter: BiquadFilterNode;
  source: AudioBufferSourceNode;
}

export class SoundSystem {
  context: AudioContext | undefined;
  private master: GainNode | undefined;
  private readonly buses = new Map<Bus, GainNode>();
  private readonly cabin = new Map<Bus, BiquadFilterNode>();
  private reverbSend: GainNode | undefined;
  private worklet: Promise<void> | undefined;
  private workletLoaded = false;
  private readonly bank = new Map<string, AudioBuffer>();
  settings = loadSettings();
  /** Called when the bank has every sound: the beds can start. */
  onBankReady: (() => void) | undefined;
  bankReady = false;

  constructor() {
    // Browsers let a page make sound only after a gesture: make the context on the first one.
    const start = () => {
      this.start();
      if (this.context !== undefined && this.context.state === 'suspended' && !this.settings.muted) void this.context.resume().catch(() => undefined);
      if (this.context?.state === 'running') {
        window.removeEventListener('keydown', start, true);
        window.removeEventListener('pointerdown', start, true);
        window.removeEventListener('touchstart', start, true);
      }
    };
    window.addEventListener('keydown', start, true);
    window.addEventListener('pointerdown', start, true);
    window.addEventListener('touchstart', start, true);
  }

  /** Running and allowed to sound. */
  get live(): boolean {
    return this.context !== undefined && this.context.state === 'running' && !this.settings.muted;
  }

  /** The engine worklet is loaded: engines can be made. */
  get enginesReady(): boolean {
    return this.workletLoaded;
  }

  private start(): void {
    if (this.context !== undefined) return;
    let ctx: AudioContext;
    try {
      ctx = new AudioContext({ latencyHint: 'interactive' });
    } catch {
      return;
    }
    this.context = ctx;
    const limiter = ctx.createDynamicsCompressor();
    limiter.threshold.value = -6;
    limiter.knee.value = 6;
    limiter.ratio.value = 12;
    limiter.attack.value = 0.003;
    limiter.release.value = 0.2;
    this.master = ctx.createGain();
    this.master.connect(limiter).connect(ctx.destination);
    // The reverb: a decaying burst of noise, as a room's echo is.
    const reverb = ctx.createConvolver();
    reverb.buffer = impulse(ctx, 2.2);
    this.reverbSend = ctx.createGain();
    this.reverbSend.gain.value = 0;
    this.reverbSend.connect(reverb).connect(this.master);
    for (const bus of ['engine', 'world', 'effects'] as const) {
      const gain = ctx.createGain();
      const filter = ctx.createBiquadFilter();
      filter.type = 'lowpass';
      filter.frequency.value = 20000;
      filter.Q.value = 0.5;
      gain.connect(filter).connect(this.master);
      filter.connect(this.reverbSend);
      this.buses.set(bus, gain);
      this.cabin.set(bus, filter);
    }
    this.applySettings();
    this.worklet = ctx.audioWorklet.addModule(workletUrl).then(() => { this.workletLoaded = true; }, (e: unknown) => {
      console.warn('Engine sound unavailable:', e);
    });
    this.loadBank(ctx);
  }

  private loadBank(ctx: AudioContext): void {
    const worker = new Worker(new URL('../../workers/soundbank.worker.ts', import.meta.url), { type: 'module' });
    worker.onmessage = (event: MessageEvent<BankSound | 'done'>) => {
      if (event.data === 'done') {
        worker.terminate();
        this.bankReady = true;
        this.onBankReady?.();
        return;
      }
      const { id, rate, samples } = event.data;
      const buffer = ctx.createBuffer(1, samples.length, rate);
      buffer.copyToChannel(samples as Float32Array<ArrayBuffer>, 0);
      this.bank.set(id, buffer);
    };
    worker.postMessage('make');
  }

  /** The input of `bus`, for sounds made elsewhere (the horn, the sirens). */
  input(bus: Bus): AudioNode | undefined {
    return this.buses.get(bus);
  }

  has(id: string): boolean {
    return this.bank.has(id);
  }

  setMuted(muted: boolean): void {
    this.settings.muted = muted;
    this.applySettings();
    if (this.context !== undefined) {
      // Muted, the audio thread rests too.
      if (muted) void this.context.suspend().catch(() => undefined);
      else void this.context.resume().catch(() => undefined);
    }
    this.save();
  }

  setLevel(level: Level, value: number): void {
    this.settings[level] = Math.max(0, Math.min(1, value));
    this.applySettings();
    this.save();
  }

  private save(): void {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(this.settings));
    } catch {
      // Not remembered; applied all the same.
    }
  }

  private applySettings(): void {
    const ctx = this.context;
    if (ctx === undefined || this.master === undefined) return;
    const s = this.settings;
    this.master.gain.setTargetAtTime(s.muted ? 0 : s.master * s.master, ctx.currentTime, 0.01);
    for (const [bus, gain] of this.buses) gain.gain.setTargetAtTime(s[bus] * s[bus], ctx.currentTime, 0.02);
  }

  /**
   * In the cabin (first person in a car): the engine through the bulkhead,
   * the world through the glass. Outside, everything open.
   */
  setCabin(inside: boolean): void {
    const ctx = this.context;
    if (ctx === undefined) return;
    const t = ctx.currentTime;
    this.cabin.get('engine')!.frequency.setTargetAtTime(inside ? 2600 : 20000, t, 0.15);
    this.cabin.get('world')!.frequency.setTargetAtTime(inside ? 1100 : 20000, t, 0.15);
  }

  /** How much echo: a tunnel's lots, a street's a little. */
  setReverb(amount: number): void {
    const ctx = this.context;
    if (ctx === undefined || this.reverbSend === undefined) return;
    this.reverbSend.gain.setTargetAtTime(Math.max(0, Math.min(1, amount)), ctx.currentTime, 0.3);
  }

  /** Put the listener at the camera: `x`…, looking along `forward`, `up` up. */
  listen(at: At, forward: At, up: At): void {
    const ctx = this.context;
    if (ctx === undefined) return;
    const l = ctx.listener;
    const t = ctx.currentTime;
    if (l.positionX !== undefined) {
      l.positionX.setTargetAtTime(at.x, t, 0.01);
      l.positionY.setTargetAtTime(at.y, t, 0.01);
      l.positionZ.setTargetAtTime(at.z, t, 0.01);
      l.forwardX.setTargetAtTime(forward.x, t, 0.01);
      l.forwardY.setTargetAtTime(forward.y, t, 0.01);
      l.forwardZ.setTargetAtTime(forward.z, t, 0.01);
      l.upX.setTargetAtTime(up.x, t, 0.01);
      l.upY.setTargetAtTime(up.y, t, 0.01);
      l.upZ.setTargetAtTime(up.z, t, 0.01);
    } else {
      // Firefox before 2024: the old setters.
      l.setPosition(at.x, at.y, at.z);
      l.setOrientation(forward.x, forward.y, forward.z, up.x, up.y, up.z);
    }
  }

  /** A panner at `at`, heard in full to `near` metres and fading as distance doubles. */
  panner(at: At, near = 5): PannerNode | undefined {
    const ctx = this.context;
    if (ctx === undefined) return undefined;
    const p = ctx.createPanner();
    p.panningModel = 'equalpower';
    p.distanceModel = 'inverse';
    p.refDistance = near;
    p.rolloffFactor = 1;
    p.maxDistance = 20000;
    place(p, at, ctx.currentTime, true);
    return p;
  }

  /** Play a sound from the bank. Returns its source, or undefined if it cannot sound yet. */
  play(id: string, o: PlayOptions): AudioBufferSourceNode | undefined {
    const ctx = this.context, buffer = this.bank.get(id), bus = this.buses.get(o.bus);
    if (ctx === undefined || buffer === undefined || bus === undefined || !this.live) return undefined;
    const source = ctx.createBufferSource();
    source.buffer = buffer;
    source.playbackRate.value = o.rate ?? 1;
    const gain = ctx.createGain();
    gain.gain.value = o.gain ?? 1;
    source.connect(gain);
    const panner = o.at === undefined ? undefined : this.panner(o.at, o.near);
    if (panner !== undefined) gain.connect(panner).connect(bus);
    else gain.connect(bus);
    const when = ctx.currentTime + (o.delay ?? 0);
    if (o.seconds !== undefined) {
      source.loop = true;
      gain.gain.setValueAtTime(0, when);
      gain.gain.linearRampToValueAtTime(o.gain ?? 1, when + Math.min(0.015, o.seconds));
      gain.gain.setValueAtTime(o.gain ?? 1, when + o.seconds);
      gain.gain.linearRampToValueAtTime(0, when + o.seconds + 0.08);
      source.start(when, Math.random() * buffer.duration);
      source.stop(when + o.seconds + 0.1);
    } else {
      source.start(when);
    }
    // Let the graph go once it has played.
    source.onended = () => {
      gain.disconnect();
      panner?.disconnect();
    };
    return source;
  }

  /** A looping bed through a filter, silent until its level is set. */
  bed(id: string, bus: Bus, filter: BiquadFilterType = 'allpass', frequency = 1000, q = 0.7): Bed | undefined {
    const ctx = this.context, buffer = this.bank.get(id), out = this.buses.get(bus);
    if (ctx === undefined || buffer === undefined || out === undefined) return undefined;
    const source = ctx.createBufferSource();
    source.buffer = buffer;
    source.loop = true;
    const f = ctx.createBiquadFilter();
    f.type = filter;
    f.frequency.value = frequency;
    f.Q.value = q;
    const gain = ctx.createGain();
    gain.gain.value = 0;
    source.connect(f).connect(gain).connect(out);
    // Two beds from one buffer should not be in step.
    source.start(0, Math.random() * buffer.duration);
    return { gain, filter: f, source };
  }

  /**
   * A loop from the bank placed in the scene: silent until its gain is set,
   * moved with `place` on its panner, and let go with `stop` on its source.
   */
  loopAt(id: string, bus: Bus, at: At, near = 8): { source: AudioBufferSourceNode; gain: GainNode; panner: PannerNode } | undefined {
    const ctx = this.context, buffer = this.bank.get(id), out = this.buses.get(bus);
    if (ctx === undefined || buffer === undefined || out === undefined) return undefined;
    const panner = this.panner(at, near);
    if (panner === undefined) return undefined;
    const source = ctx.createBufferSource();
    source.buffer = buffer;
    source.loop = true;
    const gain = ctx.createGain();
    gain.gain.value = 0;
    source.connect(gain).connect(panner).connect(out);
    source.start(0, Math.random() * buffer.duration);
    source.onended = () => { gain.disconnect(); panner.disconnect(); };
    return { source, gain, panner };
  }

  /** An engine on the audio thread, through a panner. Undefined until the worklet has loaded. */
  engine(kind: EngineKind, seed: number, running: boolean, params: Partial<EngineParams>, at: At, near = 4): EngineVoice | undefined {
    const ctx = this.context, bus = this.buses.get('engine');
    if (ctx === undefined || bus === undefined || !this.workletLoaded) return undefined;
    const options: EngineOptions = { kind, seed, running, params };
    let node: AudioWorkletNode;
    try {
      node = new AudioWorkletNode(ctx, 'engine', { numberOfInputs: 0, numberOfOutputs: 1, outputChannelCount: [1], processorOptions: options });
    } catch {
      return undefined;
    }
    const panner = this.panner(at, near)!;
    node.connect(panner).connect(bus);
    return new EngineVoice(ctx, node, panner, kind);
  }
}

export function place(p: PannerNode, at: At, t: number, now = false): void {
  if (p.positionX !== undefined) {
    if (now) {
      p.positionX.value = at.x;
      p.positionY.value = at.y;
      p.positionZ.value = at.z;
    } else {
      p.positionX.setTargetAtTime(at.x, t, 0.02);
      p.positionY.setTargetAtTime(at.y, t, 0.02);
      p.positionZ.setTargetAtTime(at.z, t, 0.02);
    }
  } else {
    p.setPosition(at.x, at.y, at.z);
  }
}

/** One engine heard: its synthesiser on the audio thread and where it is. */
export class EngineVoice {
  private last: Partial<EngineParams> = {};
  private sentAt = 0;

  constructor(
    private readonly ctx: AudioContext,
    private readonly node: AudioWorkletNode,
    private readonly panner: PannerNode,
    readonly kind: EngineKind,
  ) {}

  /**
   * New targets for the synthesiser: sent at most 40 times a second, and
   * only when something has moved, so a car standing at the lights costs
   * nothing.
   */
  set(params: Partial<EngineParams>): void {
    const now = this.ctx.currentTime;
    if (now - this.sentAt < 0.025) return;
    let changed = false;
    for (const key of Object.keys(params) as (keyof EngineParams)[]) {
      const a = params[key]!, b = this.last[key];
      if (b === undefined || Math.abs(a - b) > 1e-3 * Math.max(1, Math.abs(a))) { changed = true; break; }
    }
    if (!changed) return;
    this.last = { ...this.last, ...params };
    this.sentAt = now;
    this.node.port.postMessage({ set: params } satisfies EngineMessage);
  }

  place(at: At): void {
    place(this.panner, at, this.ctx.currentTime);
  }

  /** Fade it out and let it go. */
  stop(): void {
    this.node.port.postMessage({ stop: true } satisfies EngineMessage);
    const node = this.node, panner = this.panner;
    setTimeout(() => {
      node.disconnect();
      panner.disconnect();
    }, 600);
  }
}

/** A room's echo: two channels of noise dying away over `seconds`, the high end first. */
function impulse(ctx: AudioContext, seconds: number): AudioBuffer {
  const n = Math.floor(ctx.sampleRate * seconds);
  const buffer = ctx.createBuffer(2, n, ctx.sampleRate);
  for (let c = 0; c < 2; c++) {
    const data = buffer.getChannelData(c);
    let lp = 0;
    for (let i = 0; i < n; i++) {
      const u = i / n;
      // Darker as it dies: a one-pole low-pass closing over the tail.
      const a = 0.9 - 0.85 * u;
      lp += a * ((Math.random() * 2 - 1) - lp);
      data[i] = lp * Math.pow(1 - u, 2.5) * 0.5;
    }
  }
  return buffer;
}
